import apiClient from './client';
import type { AskJeffFlag, AskJeffReviewItem, AskJeffSection } from '../types/askJeff';

/**
 * The "Ask Jeff" calls (feature 025). Every endpoint is Super Editor-only server-side — Admin
 * gets 403 like anyone else — so callers must gate on the role before invoking them rather than
 * relying on an error to tell them.
 */
export const askJeffApi = {
  /**
   * Flag a hand for Jeff, optionally with a note about the doubt. Re-flagging an already-flagged
   * hand updates the note; the section stays as first recorded.
   */
  flagHand: async (tableResultId: number, sectionKey: string, note: string | null): Promise<AskJeffFlag> => {
    const response = await apiClient.post<AskJeffFlag>('/ask-jeff/flags', {
      tableResultId,
      sectionKey,
      note,
    });
    return response.data;
  },

  /** Edit the note on an already-flagged hand. */
  updateNote: async (tableResultId: number, note: string | null): Promise<AskJeffFlag> => {
    const response = await apiClient.put<AskJeffFlag>(`/ask-jeff/flags/${tableResultId}`, { note });
    return response.data;
  },

  /**
   * Flag state for the given hands, batched. The practice pages ask once for the ids they are
   * showing; hands that are not flagged are simply absent from the result.
   */
  getFlags: async (tableResultIds: number[]): Promise<AskJeffFlag[]> => {
    if (tableResultIds.length === 0) return [];
    const response = await apiClient.get<AskJeffFlag[]>('/ask-jeff/flags', {
      params: { ids: tableResultIds.join(',') },
    });
    return response.data;
  },

  /** The sections that currently have flagged hands — the review page's filter. */
  getSections: async (): Promise<AskJeffSection[]> => {
    const response = await apiClient.get<AskJeffSection[]>('/ask-jeff/sections');
    return response.data;
  },

  /**
   * Every flagged hand with its answer revealed and Jeff's comment attached, oldest first.
   * Pass a section key to filter, or omit for all sections.
   */
  getReview: async (section?: string): Promise<AskJeffReviewItem[]> => {
    const response = await apiClient.get<AskJeffReviewItem[]>('/ask-jeff/review', {
      params: section && section !== 'all' ? { section } : undefined,
    });
    return response.data;
  },

  /**
   * Record Jeff's comment for a flagged hand — an empty comment clears it. The comment goes to
   * the shared store every practice query reads, so it shows as "JEFF SAYS" straight away.
   */
  saveComment: async (tableResultId: number, comment: string | null): Promise<void> => {
    await apiClient.put(`/ask-jeff/comments/${tableResultId}`, { comment });
  },
};
